import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogClose } from '@/components/ui/dialog';
import ChecklistCompleteForm from './checklist-complete-form';
import { useState } from 'react';
import { FindingCreateDialog } from '@/components/audit-centre/FindingCreateDialog';
import { FindingDetailDialog } from '@/components/audit-centre/FindingDetailDialog';
import { FindingEditDialog } from '@/components/audit-centre/FindingEditDialog';
import type { Finding } from '@/app/pages/auditcentre';
import { toast } from 'sonner';
import type { Checklist } from '../types/checklist.types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { cleanStandardName } from '@/components/ui/FrameworkCombobox';
import { useTenancy } from '@/contexts/TenancyContext';
import { AuditService } from '@/app/pages/auditcentre/auditService';

interface ChecklistCompleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklist: Checklist | null;
  onSubmit?: (values: Record<string, any>, findings?: Finding[]) => void;
}

const ChecklistCompleteDialog: React.FC<ChecklistCompleteDialogProps> = ({ open, onOpenChange, checklist, onSubmit }) => {
  // --- Styling helpers ---
  const frostedShellClasses = 'rounded-2xl border border-border bg-card/95 shadow-xl shadow-primary/10 overflow-hidden';
  const subtleHeadingClasses = 'text-xs font-semibold uppercase tracking-[0.18em] text-primary/70';
  const statBadgeClasses = 'border-primary/30 bg-primary/10 text-primary';

  // --- Submitting state for form/button ---
  const [submitting, setSubmitting] = useState(false);
  // --- Findings state management ---
  const [findings, setFindings] = useState<Finding[]>(checklist?.findings || []);
  // Sync findings state with checklist prop when dialog opens or checklist changes
  React.useEffect(() => {
    setFindings(checklist?.findings || []);
  }, [open, checklist]);
  const [showCreateFinding, setShowCreateFinding] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null);
  const [showDetailFinding, setShowDetailFinding] = useState(false);
  const [showEditFinding, setShowEditFinding] = useState(false);
  const { activeTenancy, userIdToName } = useTenancy();
  const auditService = React.useMemo(() => (activeTenancy ? new AuditService({ activeTenancy }) : null), [activeTenancy]);
  const [auditOptions, setAuditOptions] = React.useState<{ id: string; label: string }[]>([]);
  const [isLoadingAudits, setIsLoadingAudits] = React.useState(false);

  React.useEffect(() => {
    if (!auditService || !open) {
      if (!open) {
        setAuditOptions([]);
      }
      return;
    }

    let isMounted = true;
    const loadAudits = async () => {
      try {
        setIsLoadingAudits(true);
        const audits = await auditService.listAudits();
        if (!isMounted) return;
        setAuditOptions(
          audits.map(audit => ({
            id: audit.id,
            label: [audit.type, audit.scope].filter(Boolean).join(' — '),
          }))
        );
      } catch (error: any) {
        if (isMounted) {
          toast.error(error?.message || 'Failed to load audits.');
          setAuditOptions([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAudits(false);
        }
      }
    };

    loadAudits();
    return () => {
      isMounted = false;
    };
  }, [auditService, open]);

  // Wrap onSubmit to handle dialog close and toast
  const handleSubmit = async (values: Record<string, any>) => {
    if (!onSubmit) return;
    try {
      console.log('Submitting checklist with findings:', { values, findings });
      // Replace undefined with null for findings
      /*
      {
    "values": {
        "item1": true
    },
    "findings": [
        {
            "id": "1752547262838-0.5453085820251828",
            "summary": "etwstw",
            "owner": "Dkqueio5g9hCzVzB8MUqRNYL17E2",
            "status": "Open",
            "type": "Minor NC",
            "correctiveActions": [],
            "origin": "standalone",
            "createdAt": "2025-07-15T02:41:02.838Z",
            "updatedAt": "2025-07-15T02:41:02.838Z",
            "comments": ""
        }
    ]
}*/
      if (!findings) {
        await onSubmit({ ...values }, []);
      } else {
        const findingsWithNull = findings.map(f => {
          const updatedFinding = { ...f };
          for (const key of Object.keys(updatedFinding)) {
            if (updatedFinding[key] === undefined) {
              updatedFinding[key] = null;
            }
          }
          return updatedFinding;
        });
        await onSubmit({ ...values, findings: findingsWithNull });
      }
      onOpenChange(false);
      toast.success('Checklist submitted!');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit checklist');
    }
  };

  const frameworks = React.useMemo(() => {
    if (!checklist) return [] as string[];
    if (Array.isArray(checklist.standards) && checklist.standards.length) return checklist.standards;
    if (checklist.standard) return [checklist.standard];
    return [] as string[];
  }, [checklist]);

  const primaryFrameworkLabel = frameworks.length > 1 ? `${frameworks.length} frameworks` : cleanStandardName(frameworks[0]);

  const computeProgress = React.useCallback((source?: Checklist | null) => {
    if (!source) {
      return { totalItems: 0, evidenceRequiredCount: 0, completedCount: 0, requiredSatisfied: 0, requiredTotal: 0 };
    }
    const totalItems = source.items?.length || 0;
    const evidenceRequiredCount = source.items?.filter(item => item.requireEvidence).length || 0;
    return { totalItems, evidenceRequiredCount, completedCount: 0, requiredSatisfied: 0, requiredTotal: evidenceRequiredCount };
  }, []);

  const [progress, setProgress] = React.useState(() => computeProgress(checklist));

  React.useEffect(() => {
    setProgress(computeProgress(checklist));
  }, [checklist, computeProgress]);

  const canSubmit = React.useMemo(() => {
    if (submitting) return false;
    if (!progress) return false;
    if (progress.requiredTotal > 0 && progress.requiredSatisfied < progress.requiredTotal) return false;
    if (progress.totalItems > 0 && progress.completedCount < progress.totalItems) return false;
    return true;
  }, [progress, submitting]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noScroll className="max-w-5xl! !max-h-none overflow-visible border-none bg-transparent p-0 shadow-none">
        <div className={`${frostedShellClasses} flex flex-col max-h-[calc(100vh-6rem)] overflow-y-auto`}>
          {checklist ? (
            <>
              <div className="border-b border-border/70 px-8 py-6">
                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-3">
                    <span className={subtleHeadingClasses}>Checklist Completion</span>
                    <h2 className="text-2xl font-semibold text-foreground">{checklist.title}</h2>
                    {checklist.description && (
                      <p className="max-w-xl text-sm text-muted-foreground">
                        {checklist.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-start gap-3 text-sm md:items-end">
                    <div className="flex flex-wrap gap-2">
                      {!!primaryFrameworkLabel && (
                        <Badge variant="outline" className={statBadgeClasses}>
                          {primaryFrameworkLabel}
                        </Badge>
                      )}
                      <Badge variant="outline" className={statBadgeClasses}>
                        {checklist.items.length} item{checklist.items.length === 1 ? '' : 's'}
                      </Badge>
                      {typeof checklist.completionHistory?.length === 'number' && checklist.completionHistory.length > 0 && (
                        <Badge variant="secondary" className={statBadgeClasses}>
                          {checklist.completionHistory.length} past completion{checklist.completionHistory.length === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8 px-6 py-6 md:px-8 md:py-8">
                <div className="grid gap-8 lg:grid-cols-[minmax(0,1.7fr),minmax(0,1fr)]">
                  <div>
                    <ChecklistCompleteForm
                      checklist={checklist}
                      onSubmit={handleSubmit}
                      submitting={submitting}
                      setSubmitting={setSubmitting}
                      onProgressChange={setProgress}
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="rounded-xl border border-border/60 bg-muted/30 p-5">
                      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <span className={subtleHeadingClasses}>Linked Findings</span>
                          <h3 className="text-lg font-semibold text-foreground">Non-conformities & notes</h3>
                          <p className="text-xs text-muted-foreground">
                            Add findings to capture follow-up actions while completing the checklist.
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                          onClick={() => setShowCreateFinding(true)}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Finding
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {findings.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-border/70 bg-card/80 px-4 py-6 text-sm text-muted-foreground text-center">
                            No findings added yet.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {findings.map((finding, idx) => (
                              <div
                                key={finding.id || idx}
                                className="flex gap-3 rounded-lg border border-border/60 bg-card/90 p-4 shadow-sm"
                              >
                                <div className="flex-1 space-y-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="secondary" className="border-border bg-muted text-foreground">
                                      {finding.type}
                                    </Badge>
                                    {finding.status && (
                                      <Badge
                                        variant="outline"
                                        className={finding.status === 'Closed'
                                          ? 'border-emerald-400/50 bg-emerald-100/10 text-emerald-400'
                                          : 'border-amber-400/50 bg-amber-100/10 text-amber-400'}
                                      >
                                        {finding.status}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm font-medium text-foreground">{finding.summary}</p>
                                  {finding.owner && (
                                    <p className="text-xs text-muted-foreground">Owner: {userIdToName[finding.owner]}</p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="justify-start text-primary hover:bg-primary/10"
                                    onClick={() => {
                                      setSelectedFinding(finding);
                                      setShowDetailFinding(true);
                                    }}
                                  >
                                    View
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="justify-start text-muted-foreground hover:bg-muted"
                                    onClick={() => {
                                      setSelectedFinding(finding);
                                      setShowEditFinding(true);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/70 bg-card/85 px-6 py-5 shadow-sm shadow-primary/10">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2">
                        <span className={subtleHeadingClasses}>Checklist Response</span>
                        <p className="max-w-xs text-sm text-muted-foreground">
                          Track progress while completing checklist items and uploading evidence.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Badge variant="secondary" className={statBadgeClasses}>
                          {progress.completedCount}/{progress.totalItems} complete
                        </Badge>
                        <Badge variant="secondary" className={statBadgeClasses}>
                          {progress.requiredSatisfied}/{progress.requiredTotal || progress.evidenceRequiredCount} evidence done
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="border-t border-border/70 bg-muted/30 px-6 py-5 md:px-8">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <Button
                    variant="default"
                    className="w-full px-6 py-3 sm:w-auto sm:min-w-[160px]"
                    onClick={() => {
                      const form = document.querySelector('.checklist-complete-form') as HTMLFormElement;
                      if (form) form.requestSubmit();
                    }}
                    disabled={!canSubmit}
                  >
                    {submitting ? 'Submitting…' : 'Submit Checklist'}
                  </Button>
                  <DialogClose asChild>
                    <Button
                      variant="ghost"
                      className="w-full sm:w-auto sm:min-w-[140px]"
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                </div>
              </DialogFooter>
            </>
          ) : (
            <div className="p-10 text-center text-muted-foreground">Loading checklist…</div>
          )}
        </div>

        {/* Create Finding Dialog */}
        <FindingCreateDialog
          open={showCreateFinding}
          onOpenChange={setShowCreateFinding}
          onCreate={async finding => {
            // Ensure all required Finding fields are present
            const now = new Date();
            const baseFinding = {
              summary: finding.summary,
              owner: finding.owner,
              status: finding.status,
              type: (['OFI', 'Minor NC', 'Major NC'].includes(finding.type) ? finding.type : 'OFI') as 'OFI' | 'Minor NC' | 'Major NC',
              auditId: finding.auditId ?? null,
              correctiveActions: [],
              origin: 'standalone' as const,
              createdAt: now,
              updatedAt: now,
              comments: finding.comments || '',
            } satisfies Omit<Finding, 'id'>;
            if (!auditService) {
              toast.error('Unable to create finding. Please try again.');
              setShowCreateFinding(true);
              return;
            }
            try {
              const createdId = await auditService.createStandaloneFinding(baseFinding);
              const fullFinding: Finding = { id: createdId, ...baseFinding };
              setFindings(prev => [...prev, fullFinding]);
              toast.success('Finding created.');
            } catch (error: any) {
              toast.error(error?.message || 'Failed to create finding.');
              setShowCreateFinding(true);
            }
          }}
          auditOptions={auditOptions}
          defaultAuditId={checklist && 'auditId' in checklist ? (checklist as any).auditId ?? undefined : undefined}
        />
        {/* Detail Dialog */}
        <FindingDetailDialog
          open={showDetailFinding}
          onOpenChange={setShowDetailFinding}
          finding={selectedFinding}
          onActionCreated={() => { }}
          onActionUpdated={() => { }}
          onActionDeleted={() => { }}
          auditService={auditService}
        />
        {/* Edit Dialog */}
        <FindingEditDialog
          open={showEditFinding}
          onOpenChange={setShowEditFinding}
          finding={selectedFinding}
          onSave={async updated => {
            const currentId = selectedFinding?.id;
            if (!currentId) {
              return false;
            }
            if (!auditService) {
              toast.error('Unable to update finding. Please try again.');
              return false;
            }
            try {
              const updatePayload = { ...updated, updatedAt: new Date() } as Partial<Finding>;
              await auditService.updateStandaloneFinding(currentId, updatePayload);
              setFindings(prev => prev.map(f => (f.id === currentId ? { ...f, ...updatePayload } : f)));
              setSelectedFinding(prev => (prev && prev.id === currentId ? { ...prev, ...updatePayload } : prev));
              setShowEditFinding(false);
              toast.success('Finding updated.');
              return true;
            } catch (error: any) {
              toast.error(error?.message || 'Failed to update finding.');
              return false;
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistCompleteDialog;
